import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Edit2, Eye, Upload, Save, X, AlertCircle, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Editor from '@monaco-editor/react';

const ReadmeEditor = ({ 
  modelId, 
  initialContent = '', 
  isOwner = false, 
  onSave, 
  onCancel,
  apiService 
}) => {
  const [content, setContent] = useState(initialContent);
  const [isPreview, setIsPreview] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [isUploading, setUploading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // 'success', 'error', ''
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);

  const handleContentChange = (newContent) => {
    setContent(newContent);
    setHasUnsavedChanges(newContent !== initialContent);
    setSaveStatus('');
  };

  const handleSave = useCallback(async (isAutoSave = false) => {
    if (!content.trim() && !initialContent) return; // Don't save empty content
    
    setSaving(true);
    setSaveStatus('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/models/${modelId}/readme`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiService?.getAccessToken?.() || window.authContext?.getAccessToken?.() || ''}`
        },
        body: JSON.stringify({ content })
      });

      if (!response.ok) {
        throw new Error(`Failed to save: ${response.statusText}`);
      }

      const result = await response.json();
      
      setSaveStatus('success');
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      
      if (!isAutoSave && onSave) {
        onSave(content);
      }
      
      // Clear success status after 3 seconds
      setTimeout(() => setSaveStatus(''), 3000);
      
    } catch (error) {
      console.error('Failed to save README:', error);
      setSaveStatus('error');
      
      // Clear error status after 5 seconds
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setSaving(false);
    }
  }, [content, initialContent, modelId, apiService, onSave]);

  // Auto-save functionality
  useEffect(() => {
    if (hasUnsavedChanges && content !== initialContent) {
      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      
      // Set new auto-save timeout (5 seconds)
      autoSaveTimeoutRef.current = setTimeout(() => {
        handleSave(true); // true indicates auto-save
      }, 5000);
    }
    
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [content, hasUnsavedChanges, initialContent, handleSave]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 's') {
          e.preventDefault();
          handleSave();
        } else if (e.key === 'p') {
          e.preventDefault();
          setIsPreview(!isPreview);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPreview, handleSave]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.name.toLowerCase().endsWith('.md')) {
      setSaveStatus('error');
      alert('Please select a .md file');
      return;
    }

    if (file.size > 50000) { // 50KB limit
      setSaveStatus('error');
      alert('File too large. Maximum size is 50KB');
      return;
    }

    setUploading(true);
    setSaveStatus('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/models/${modelId}/readme/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiService?.getAccessToken?.() || window.authContext?.getAccessToken?.() || ''}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Failed to upload: ${response.statusText}`);
      }

      // Read file content to update editor
      const text = await file.text();
      setContent(text);
      setHasUnsavedChanges(false);
      setSaveStatus('success');
      setLastSaved(new Date());
      
      if (onSave) {
        onSave(text);
      }

    } catch (error) {
      console.error('Failed to upload README:', error);
      setSaveStatus('error');
      alert(`Failed to upload file: ${error.message}`);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatLastSaved = () => {
    if (!lastSaved) return '';
    const now = new Date();
    const diff = Math.floor((now - lastSaved) / 1000);
    
    if (diff < 60) return `Saved ${diff}s ago`;
    if (diff < 3600) return `Saved ${Math.floor(diff / 60)}m ago`;
    return `Saved at ${lastSaved.toLocaleTimeString()}`;
  };

  const getStatusIcon = () => {
    if (isSaving) return <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />;
    if (saveStatus === 'success') return <Check className="w-4 h-4 text-green-500" />;
    if (saveStatus === 'error') return <AlertCircle className="w-4 h-4 text-red-500" />;
    return null;
  };

  if (!isOwner && !content) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No README available for this model.</p>
      </div>
    );
  }

  // Debug logging
  console.log('ReadmeEditor props:', { modelId, initialContent, isOwner, content });

  if (!isOwner) {
    // Read-only view for non-owners
    return (
      <div className="prose prose-sm max-w-none text-gray-700">
        <ReactMarkdown
          components={{
            h1: ({children}) => <h1 className="text-xl font-bold text-gray-900 mb-3 mt-6 first:mt-0">{children}</h1>,
            h2: ({children}) => <h2 className="text-lg font-semibold text-gray-900 mb-2 mt-5 first:mt-0">{children}</h2>,
            h3: ({children}) => <h3 className="text-base font-semibold text-gray-900 mb-2 mt-4 first:mt-0">{children}</h3>,
            p: ({children}) => <p className="mb-3 text-sm leading-relaxed">{children}</p>,
            ul: ({children}) => <ul className="list-disc list-inside space-y-1 mb-3 text-sm">{children}</ul>,
            ol: ({children}) => <ol className="list-decimal list-inside space-y-1 mb-3 text-sm">{children}</ol>,
            li: ({children}) => <li className="text-sm">{children}</li>,
            code: ({children}) => <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
            pre: ({children}) => <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto mb-3">{children}</pre>,
            blockquote: ({children}) => <blockquote className="border-l-4 border-gray-300 pl-4 italic mb-3">{children}</blockquote>,
          }}
        >
          {content || 'No README content available.'}
        </ReactMarkdown>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ minHeight: '600px' }}>
      {/* Toolbar */}
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsPreview(!isPreview)}
            className={`flex items-center space-x-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              isPreview 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {isPreview ? <Edit2 className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span>{isPreview ? 'Edit' : 'Preview'}</span>
          </button>

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex items-center space-x-2 px-3 py-1.5 rounded text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            <Upload className="w-4 h-4" />
            <span>{isUploading ? 'Uploading...' : 'Upload .md'}</span>
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".md"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>

        <div className="flex items-center space-x-3">
          {/* Status indicator */}
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            {getStatusIcon()}
            {isSaving ? (
              <span>Saving...</span>
            ) : saveStatus === 'success' ? (
              <span className="text-green-600">Saved</span>
            ) : saveStatus === 'error' ? (
              <span className="text-red-600">Save failed</span>
            ) : hasUnsavedChanges ? (
              <span className="text-orange-600">Unsaved changes</span>
            ) : lastSaved ? (
              <span>{formatLastSaved()}</span>
            ) : null}
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => handleSave()}
              disabled={isSaving || !hasUnsavedChanges}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              <Save className="w-4 h-4" />
              <span>Save</span>
            </button>
            
            <button
              onClick={onCancel}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm font-medium"
            >
              <X className="w-4 h-4" />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      </div>

      {/* Editor/Preview Content */}
      <div className="flex-1 overflow-hidden">
        {isPreview ? (
          <div className="h-full overflow-y-auto p-6">
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown
                components={{
                  h1: ({children}) => <h1 className="text-xl font-bold text-gray-900 mb-3 mt-6 first:mt-0">{children}</h1>,
                  h2: ({children}) => <h2 className="text-lg font-semibold text-gray-900 mb-2 mt-5 first:mt-0">{children}</h2>,
                  h3: ({children}) => <h3 className="text-base font-semibold text-gray-900 mb-2 mt-4 first:mt-0">{children}</h3>,
                  p: ({children}) => <p className="mb-3 text-sm leading-relaxed">{children}</p>,
                  ul: ({children}) => <ul className="list-disc list-inside space-y-1 mb-3 text-sm">{children}</ul>,
                  ol: ({children}) => <ol className="list-decimal list-inside space-y-1 mb-3 text-sm">{children}</ol>,
                  li: ({children}) => <li className="text-sm">{children}</li>,
                  code: ({children}) => <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                  pre: ({children}) => <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto mb-3">{children}</pre>,
                  blockquote: ({children}) => <blockquote className="border-l-4 border-gray-300 pl-4 italic mb-3">{children}</blockquote>,
                }}
              >
                {content || 'Start writing your README...'}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col">
            {/* Monaco Editor with fallback */}
            <div 
              className="flex-1 min-h-[400px] border border-gray-200 rounded"
              onClick={() => {
                // Manual focus attempt if clicking the container
                if (editorRef.current) {
                  try {
                    editorRef.current.focus();
                  } catch (e) {
                    console.warn('Manual focus failed:', e);
                  }
                }
              }}
            >
              <Editor
                height="100%"
                language="markdown"
                value={content}
                onChange={handleContentChange}
                onMount={(editor) => { 
                  editorRef.current = editor; 
                  console.log('Monaco editor mounted:', editor);
                  // Focus the editor after mount
                  setTimeout(() => {
                    try {
                      editor.focus();
                      editor.setPosition({ lineNumber: 1, column: 1 });
                    } catch (e) {
                      console.warn('Could not focus Monaco editor:', e);
                    }
                  }, 200);
                }}
                beforeMount={(monaco) => {
                  console.log('Monaco instance:', monaco);
                }}
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  fontFamily: '"Monaco", "Menlo", "Ubuntu Mono", "Consolas", "source-code-pro", monospace',
                  lineNumbers: 'on',
                  wordWrap: 'on',
                  automaticLayout: true,
                  scrollBeyondLastLine: false,
                  padding: { top: 16, bottom: 16 },
                  readOnly: false,
                  selectOnLineNumbers: true,
                  mouseWheelZoom: false,
                  contextmenu: true,
                  quickSuggestions: false,
                  acceptSuggestionOnEnter: 'off',
                  tabCompletion: 'off',
                  wordBasedSuggestions: false,
                  suggestOnTriggerCharacters: false,
                  parameterHints: { enabled: false },
                  snippetSuggestions: 'none'
                }}
                theme="vs-light"
                loading={<div className="flex items-center justify-center h-full text-gray-500">Loading Monaco editor...</div>}
              />
            </div>
            
            {/* Fallback textarea if Monaco fails */}
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-2">
                <summary className="text-xs text-gray-500 cursor-pointer">Fallback Editor (Debug)</summary>
                <textarea
                  value={content}
                  onChange={(e) => handleContentChange(e.target.value)}
                  placeholder="Enter README content..."
                  className="w-full h-32 p-3 border border-gray-300 rounded text-sm font-mono resize-none"
                />
              </details>
            )}
          </div>
        )}
      </div>
      
      {/* Help text */}
      <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500">
        <span>Tip: Use Ctrl+S to save, Ctrl+P to toggle preview. Auto-saves after 5 seconds of inactivity.</span>
      </div>
    </div>
  );
};

export default ReadmeEditor;